// @flow
import React from 'react';
import gql from 'graphql-tag';
import {compose} from 'redux';
import {connect} from 'react-redux';
import HitInfoMobile from './HitInfoMobile';
import HitInfoDesktop from './HitInfoDesktop';
import {media} from '../../utils/breakpoints';
import {injectIntl} from 'react-intl';
import {listingHasPrice} from '../../utils/priceUtils';
import {isDateless} from '../../utils/searchParamsUtils';
import {fragments as geoDistanceFragments} from '../GeoDistance/GeoDistance';
import {fragments as priceFragments} from '../Price/Price';
import {fragments as detailsFragments} from '../Details/Details';
import {fragments as ratingFragments} from '../Rating/Rating';

import type {IntlShape} from 'react-intl';
import type {ListingShape as DetailsListingShape} from '../Details/Details';
import type {Shape as GeoDistanceShape} from '../GeoDistance/GeoDistance';
import type {ListingShape as HitListingShape} from '../Hit/Hit';
import type {UnitMessage as UnitMessageShape} from '../HitInfo/HitInfoDesktop';

import type {PriceListingShape} from '../Price/Price';
import type {ListingShape as RatingListingShape} from '../Rating/Rating';
import type {State as ReduxState} from '../../reducers/index';

import {PP_ACCEPTED} from '../../constants/badges';

import './HitInfo.less';

export const fragments = {
    listing: gql`
        fragment HitInfoListing on Listing {
            ...DetailsListing
            ...PriceListing
            ...RatingListing
            ...GeoDistanceListing
            detailPageUrl
            instantBookable
            minStayRange {
                minStayHigh
                minStayLow
            }
            listingId
            partnerBadges {
                id
                helpText
                name
            }
            rankedBadges(rankingStrategy: SERP) {
                id
                helpText
                name
            }
            propertyId
            listingNumber
            propertyType
            propertyMetadata {
                headline
                propertyName
            }
            reviewBadges {
                id
                name
                helpText
            }
            unitMessage(assetVersion: 1) {
                iconText {
                    message
                    icon
                    messageValueType
                }
            }
        }
        ${detailsFragments.listing}
        ${priceFragments.listing}
        ${ratingFragments.listing}
        ${geoDistanceFragments.listing}
    `
};

export type Badge = {
    id: string,
    name: string,
    helpText?: string
};

export type ListingShape = DetailsListingShape &
    PriceListingShape &
    RatingListingShape &
    GeoDistanceShape & {
        detailPageUrl: string,
        instantBookable: boolean,
        listingId: string,
        minStayRange: {
            minStayHigh: number,
            minStayLow: number
        },
        partnerBadges: ?(Badge[]),
        rankedBadges: ?(Badge[]),
        amenitiesBadges: ?(Badge[]),
        propertyId: string,
        listingNumber: number,
        listingNamespace: string,
        propertyMetadata: ?{
            headline: string,
            propertyName: string
        },
        reviewBadges: ?(Badge[]),
        unitMessage?: UnitMessageShape
    };

type StateProps = {
    brand: string,
    isDatelessSearch: boolean,
    isHovered: boolean,
    isDesktop: boolean,
    isMetric: boolean,
    fullLocale: string,
    showDesktopHits: boolean,
    showIncludesTaxesAndFeesMessage: boolean
};

type IntlProps = {
    intl: IntlShape
};

export type OwnProps = {
    hitIndex?: number,
    isFreeCancellationOff: boolean,
    listing: HitListingShape
} & IntlProps;

export type Props = OwnProps & StateProps;

export function HitInfo(props: Props) {
    const {listing, showDesktopHits} = props;

    // Hit Badges
    let partnerBadges = listing.partnerBadges || [];
    let rankedBadges = listing.rankedBadges || [];
    let premierPartnerBadge = partnerBadges.find((e) => e.id === PP_ACCEPTED);

    const propsToPass = {
        ...props,
        premierPartnerBadge,
        rankedBadges
    };

    if (showDesktopHits) {
        return <HitInfoDesktop {...propsToPass} />;
    }
    return (
        <HitInfoMobile
            {...{
                ...propsToPass
            }}
        />
    );
}

const mapStateToProps = (
    {
        searchParams: {params},
        map: {listingId: hoveredListingId},
        system: {brand, isDesktop, isMetric, fullLocale, siteFeatures},
        viewport: {breakpoint}
    }: ReduxState,
    {listing}: OwnProps
) => {
    const isHovered = listing.listingId === hoveredListingId && isDesktop;
    const isDatelessSearch = isDateless(params);
    return {
        brand,
        isDatelessSearch,
        isDesktop,
        isHovered,
        isMetric,
        showDesktopHits: media(breakpoint, {
            minWidth: 'SM_MIN'
        }),
        // UK-only regulatory requirement, gated on global-config's `siteFeatures.showTotal`,
        // which will be flipped to `true` in production for homeaway_uk
        // 9/1/2019 (is already `true` in test & stage).
        showIncludesTaxesAndFeesMessage:
            !isDatelessSearch &&
            siteFeatures.showTotal &&
            fullLocale === 'en_GB_HA' &&
            listingHasPrice(listing),
        siteFeatures,
        fullLocale
    };
};

export default compose(
    injectIntl,
    connect(mapStateToProps)
)(HitInfo);
